<template>
  <div :class="$b({ layout })">
    <div :class="$b('form')">
      <VisionTextInput
        id="subscribe-email"
        v-model:value="email"
        type="email"
        :shape="roundedForm ? 'rounded' : 'square'"
        placeholder="you@email.com"
        :class="$b('input')"
        :disabled="subscribed"
        name="subscribeEmail"
        @keyup.enter="submit"
      />
      <ClientOnly v-if="subscribeWithPhoneNumber && showPhoneInput">
        <TelephoneInput
          id="subscribeTelepehoneInput"
          v-model="phone"
          name="telephone"
          @is-valid="phoneValid = $event"
        />
      </ClientOnly>
      <VisionButton
        :square="roundedForm ? false : true"
        :background-colour="roundedForm ? 'gradient' : 'white'"
        hover-colour="gradient"
        :disabled="loading || subscribed"
        :class="$b('submit-button')"
        @click="submit"
      >
        <template #content>
          <span
            :class="$b('submit-button-text', { loading })"
          >
            Sign up
          </span>
          <VisionLoadingSpinner
            :loading="loading"
            size="small"
            :spinner-colour="roundedForm ? 'white' : 'blue'"
            :class="$b('submit-button-loading')"
          />
        </template>
      </VisionButton>
    </div>

    <div :class="$b('alert')">
      <SlideDownTransition
        v-if="message || email"
        group
      >
        <p
          v-if="message"
          key="message"
          :class="$b('form-msg')"
        >
          {{ message }}
        </p>

        <p
          v-else-if="email"
          key="privacyNote"
          :class="$b('privacy-note')"
        >
          You can unsubscribe at any time, we treat your information with
          respect.
          <VisionLink
            line-link
            display="inline-block"
            size="inherit"
            href="/info/privacy-policy"
            target="_blank"
          >
            Privacy Policy
          </VisionLink>
        </p>
      </SlideDownTransition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SubscribeForm',

  props: {
    layout: {
      type: String,
      default: 'row',
      validator(value) {
        return ['row', 'column'].includes(value)
      },
    },
    promotionIds: {
      type: String,
      default: null,
    },
    showPhoneInput: {
      type: Boolean,
      default: true
    },
    roundedForm: {
      type: Boolean,
      default: true
    }
  },

  emits: ['submitted'],

  setup() {
    const userStore = useUserStore()
    const sidebarStore = useSidebarStore()
    const globalConfigStore = useGlobalConfigStore()

    return { userStore, sidebarStore, globalConfigStore }
  },

  data: () => ({
    email: null,
    phone: null,
    phoneValid: false,
    message: null,
    subscribed: false,
    loading: false,
  }),

  computed: {
    subscribeWithPhoneNumber() {
      return this.globalConfigStore.isFlagEnabled('subscribe-with-phone-number')
    },
  },

  watch: {
    email() {
      this.message = null
    },
  },

  methods: {
    async submit() {
      if(!this.validate()) return

      this.loading = true

      try {
        const response = await $fetch(
          `/subscribe`,
          {
            method: 'POST',
            body: {
              email: this.email,
              phone: this.phone,
              promotion_rule_ids: this.promotionIds?.split(',') || [],
              promotion_mode: 'welcome_new_user'
            },
            params: {
              locale: this.$i18n.locale.value
            }
          }
        )

        if (response.subscribed) {
          this.subscribed = true
          this.message = 'Welcome to our mailing list!'
          this.userStore.setUserEmail(this.email)
          this.$track.updateUserData()

          this.$emit('submitted', {
            subscribed: this.subscribed,
            discountCode: response.discountCode,
            hasAlreadySubscribed: response.couponErrorMessage === 'already_subscribed'
          })
        } else if ('valid' in response && !response.valid) {
          this.$track.form.invalid(response.reason)
          if (response.reason === 'already_subscribed') {
            this.message = 'That email address has already signed up.'
          } else {
            this.message = 'Please enter a valid email address and try again.'
          }
        } else {
          this.message = 'An error occurred, please try again.'
          this.loading = false
        }
      } catch (error) {
        this.$sentry.captureException(error)
        this.message = 'An error occured, please try again'
      }

      this.loading = false
    },

    validate() {
      if (!this.email) {
        this.message = 'Please enter your email'
        return false
      }

      if ((this.phone && !this.phoneValid) && this.subscribeWithPhoneNumber) {
        this.message = 'Please enter a valid phone number'
        return false
      }

      return true
    }
  },
}
</script>

<style lang="postcss">
.SubscribeForm {
  @apply relative;
}

.SubscribeForm--layout-column {
  .SubscribeForm__form {
    @apply flex-col items-center;
  }

  .SubscribeForm__submit-button {
    @apply ml-0 mt-3;
  }

  .SubscribeForm__input {
    @apply mb-4;
  }

  .SubscribeForm__input,
  .SubscribeForm__submit-button {
    @apply w-full;
  }
}

.SubscribeForm__alert {
  @apply pt-xs;

  .SubscribeForm--in-modal &__privacy-note {
    @apply block text-sm;
  }

  .LineLink--inherit {
    @apply pb-0;
  }
}

.SubscribeForm__form {
  @apply flex;
}

.SubscribeForm__submit-button {
  @apply w-32
    relative
    items-center
    ml-3;
}

.SubscribeForm__submit-button-text {
  @apply mx-auto transition-all;

  &--loading {
    @apply opacity-0
  }
}

.SubscribeForm__privacy-note {
  @apply text-xxs;
}

.SubscribeForm__form-msg {
  @apply text-red text-xs;
}
</style>
